import React, { createContext, useContext, useMemo, useState } from 'react';
import { FeatureFlags, getInitialFlags, persistFlags } from '@/config/featureFlags';
import { SecurityMonitor } from '@/utils/security';

type Ctx = {
  flags: FeatureFlags;
  setFlag: <K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) => void;
  resetFlags: () => void;
};

const FeatureFlagContext = createContext<Ctx | null>(null);

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => getInitialFlags());

  const setFlag = async <K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) => {
    setFlags(prev => {
      const next = { ...prev, [key]: value } as FeatureFlags;
      persistFlags(next);
      return next;
    });
    try {
      await SecurityMonitor.getInstance().logSecurityEvent('feature_flag_changed', { key, value });
    } catch {}
  };

  const resetFlags = async () => {
    setFlags(getInitialFlags());
    persistFlags(getInitialFlags());
    try {
      await SecurityMonitor.getInstance().logSecurityEvent('feature_flags_reset', {});
    } catch {}
  };

  const value = useMemo(() => ({ flags, setFlag, resetFlags }), [flags]);
  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  return ctx;
}
